import React from 'react';
import Header from 'app/components/layout/Header';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Progress, Badge, Label } from 'reactstrap';
import { Link } from 'react-router-dom';
import Isvg from 'react-inlinesvg';
import { IPaymentRequest } from 'app/ducks/paymentRequests/types';
import { IPaymentCollection } from 'app/ducks/paymentCollections/types';
import {
  parseAmount, friendlyDate, simpleDateNoTime, parseStatus,
} from 'app/utils/parsers';
import Checkbox from 'app/components/general/Checkbox';
import SectionGroup from 'app/components/general/SectionGroup';
import ConnectionGroup from 'app/components/general/ConnectionGroup';
import StatusBadge from 'app/components/general/StatusBadge';
import * as paymentRequestActions from 'app/ducks/paymentRequests/actions';
import * as paymentCollectionActions from 'app/ducks/paymentCollections/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as paymentRequestsSelectors from 'app/ducks/paymentRequests/selectors';
import * as paymentCollectionsSelectors from 'app/ducks/paymentCollections/selectors';
import * as organisationsSelectors from 'app/ducks/organisations/selectors';

import CompetitionIcon from 'app/images/icons/organisation.svg';

interface IPaymentCollectionDetailProps {
  actions?: any;
  match?: any;
  organisations?: any;
  paymentRequests?: any;
  PaymentCollections?: any;
  appReady?: boolean;
}

interface IPaymentCollectionDetailState {
  initialFilter: any;
  error?: string;
}

const formatSentAtText = (dateTime: any): string => {
  const parsedDate = simpleDateNoTime(dateTime);

  if (parsedDate === 'Invalid date') {
    return 'Sent';
  }
  return `Sent at ${parsedDate}`;
};

function selector(state) {
  return {
    paymentRequests: paymentRequestsSelectors.getPaymentRequests(state),
    paymentCollections: paymentCollectionsSelectors.getPaymentCollections(state),
    organisations: organisationsSelectors.getOrganisations(state),
    appReady: loadingSelectors.appReady(state),
  };
}

const actionCreators = _.merge(
  paymentRequestActions,
  paymentCollectionActions,
);

@connect(
  selector,
  (dispatch) => ({
    actions: bindActionCreators(actionCreators, dispatch),
  }),
)
export default class PaymentCollectionDetail extends React.Component<IPaymentCollectionDetailProps, IPaymentCollectionDetailState> {
  static defaultProps: IPaymentCollectionDetailProps = {
    actions: undefined,
    match: undefined,
    paymentRequests: undefined,
    paymentCollections: undefined,
    appReady: false,
  };

  componentDidMount(): void {
    this.props.actions.getPaymentRequest(this.props.match.params.action);
    this.props.actions.getPaymentCollection(this.props.match.params.subaction);
  }

  render(): React.ReactNode {
    const paymentRequest: IPaymentRequest = this.props.paymentRequests.currentPaymentRequest;
    const paymentCollection: IPaymentCollection = this.props.paymentCollections.currentPaymentCollection;
    const progressPercentage = paymentRequest.totalAmountCollectedInCents === 0
      ? 0
      : (paymentRequest.totalAmountCollectedInCents / paymentRequest.totalAmountRequestedInCents) * 100;
    const isLoading = this.props.paymentRequests.isLoading && paymentRequest.id !== this.props.match.params.subaction;

    const reminderStatuses = {
      smsInitial: {
        enabled: paymentRequest.notificationConfiguration?.sendSmsInitialNotificationOrgCustomers || false,
        sent: paymentRequest.notificationLog?.sendSmsInitialNotificationOrgCustomers?.sent || false,
        text: paymentRequest.notificationLog?.sendSmsInitialNotificationOrgCustomers?.sent ? formatSentAtText(paymentRequest.notificationLog?.sendSmsInitialNotificationOrgCustomers.sentOn) : 'Not sent',
      },
      emailInitial: {
        enabled: paymentRequest.notificationConfiguration?.sendEmailInitialNotificationOrgCustomers || false,
        sent: paymentRequest.notificationLog?.sendEmailInitialNotificationOrgCustomers?.sent || false,
        text: paymentRequest.notificationLog?.sendEmailInitialNotificationOrgCustomers?.sent ? formatSentAtText(paymentRequest.notificationLog?.sendSmsInitialNotificationOrgCustomers.sentOn) : 'Not sent',
      },
      smsDue: {
        enabled: paymentRequest.notificationConfiguration?.sendSmsDueReminderOrgCustomers || false,
        sent: paymentRequest.notificationLog?.sendSmsDueReminderOrgCustomers?.sent || false,
        text: paymentRequest.notificationLog?.sendSmsDueReminderOrgCustomers?.sent ? formatSentAtText(paymentRequest.notificationLog?.sendSmsDueReminderOrgCustomers.sentOn) : 'Not sent',
      },
      emailDue: {
        enabled: paymentRequest.notificationConfiguration?.sendEmailDueReminderOrgCustomers || false,
        sent: paymentRequest.notificationLog?.sendEmailDueReminderOrgCustomers?.sent || false,
        text: paymentRequest.notificationLog?.sendEmailDueReminderOrgCustomers?.sent ? formatSentAtText(paymentRequest.notificationLog?.sendEmailDueReminderOrgCustomers.sentOn) : 'Not sent',
      },
    };

    return (
      <div>
        <div className="row">
          <div className="col">
            <Header
              title={paymentCollection.personName}
              isLoading={isLoading}
              goBack={`/payments/segments/requests/detail/${paymentRequest.id}`}
              badge={(
                <StatusBadge
                  className="event-card-badge"
                  paymentText={paymentCollection.status}
                />
              )}
              descriptors={
                [
                  {
                    icon: <Isvg src={CompetitionIcon} />,
                    descriptor: paymentRequest.competitionName,
                  },
                ]
              }
              metadata={[
                {
                  title: 'Date Requested',
                  value: paymentRequest.createdAt !== undefined ? friendlyDate(paymentRequest.createdAt) : '',
                },
                {
                  title: 'Type',
                  value: paymentRequest.paymentCategory === 'individual-request' ? 'Customers' : 'Teams',
                },
                {
                  title: 'Date Due',
                  value: paymentRequest.dueAt !== undefined ? simpleDateNoTime(paymentRequest.dueAt) : '',
                },
              ]}
            />
          </div>
        </div>
        <div className="main-content">
          <div className="row">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <SectionGroup title="Details" isLoading={isLoading}>
                <div className="row">
                  <div className="col col-4 text-grey">
                    Payment Request
                  </div>
                  <div className="col">
                    <Link className="primary" to={`/payments/segments/requests/detail/${paymentRequest.id}`}>{paymentRequest.description}</Link>
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Team
                  </div>
                  <div className="col">
                    {paymentCollection.groupName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Payment Status
                  </div>
                  <div className="col">
                    {parseStatus(paymentCollection.status)}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Team Section
                  </div>
                  <div className="col">
                    {paymentCollection.groupSectionName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Team Season
                  </div>
                  <div className="col">
                    {paymentCollection.groupSeasonName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Description
                  </div>
                  <div className="col">
                    {paymentRequest.description}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Amount
                  </div>
                  <div className="col">
                    {parseAmount(paymentCollection.amount)}
                  </div>
                </div>
                {/* <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Net Amount
                  </div>
                  <div className="col">
                    {parseAmount(paymentCollection.netAmountInCents)}
                  </div>
                </div> */}
              </SectionGroup>
            </div>
          </div>
          <div className="row with-margin-top-extra">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <SectionGroup title="Notifications" isLoading={isLoading}>
                {reminderStatuses.smsInitial.enabled && (
                  <div className="row">
                    <div className="col col-4 text-grey">
                      Initial Notification (SMS)
                    </div>
                    <div className="col d-flex">
                      <Checkbox
                        className="with-label"
                        checked={reminderStatuses.smsInitial.sent}
                      />
                      <Label className="d-inline no-margin-bottom">
                        {reminderStatuses.smsInitial.text}
                      </Label>
                    </div>
                  </div>
                )}
                {reminderStatuses.emailInitial.enabled && (
                  <div className="row with-margin-top-10">
                    <div className="col col-4 text-grey">
                      Initial Notification (Email)
                    </div>
                    <div className="col d-flex">
                      <Checkbox
                        className="with-label"
                        checked={reminderStatuses.emailInitial.sent}
                      />
                      <Label className="d-inline no-margin-bottom">{reminderStatuses.emailInitial.text}</Label>
                    </div>
                  </div>
                )}
                {reminderStatuses.smsDue.enabled && (
                  <div className="row with-margin-top-10">
                    <div className="col col-4 text-grey">
                      Due Today Reminder (SMS)
                    </div>
                    <div className="col d-flex">
                      <Checkbox
                        className="with-label"
                        checked={reminderStatuses.smsDue.sent}
                      />
                      <Label className="d-inline no-margin-bottom">
                        {reminderStatuses.smsDue.text}
                      </Label>
                    </div>
                  </div>
                )}
                {reminderStatuses.emailDue.enabled && (
                  <div className="row with-margin-top-10">
                    <div className="col col-4 text-grey">
                      Due Today Reminder (Email)
                    </div>
                    <div className="col d-flex">
                      <Checkbox
                        className="with-label"
                        checked={reminderStatuses.emailDue.sent}
                      />
                      <Label className="d-inline no-margin-bottom">{reminderStatuses.emailDue.text}</Label>
                    </div>
                  </div>
                )}
              </SectionGroup>
            </div>
          </div>
          <div className="row with-margin-top-extra">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <ConnectionGroup
                title="Connections"
                isLoading={isLoading}
                items={
                  [
                    {
                      type: 'customer',
                      title: 'Customer',
                      links: [{
                        name: paymentCollection.personName,
                        id: paymentCollection.person?.id,
                      }],
                    },
                    {
                      type: 'team',
                      title: 'Team',
                      links: [{
                        name: paymentCollection.groupName,
                        id: paymentCollection.group?.id,
                      }],
                    },
                  ]
                }
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
